// Small DOM + formatting helpers shared by the screens. No framework —
// these are the whole toolkit.

import { visibleTime } from './business-settings'
// The round's clock and money sign (Step 1, 29 Sep 2026). round-config imports nothing, but
// it is one more module every harness that transpiles ui.ts must bundle.
import { clockWords, currencySymbol, dayMonth, monthFirst, MONTHLY_DAYS, monthlyCycles, roundDay, shownClock, shownTime, weekdayDayMonth, yearCycles } from './round-config'

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string | null | undefined)[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v
    else if (k.startsWith('data-')) node.setAttribute(k, v)
    else node.setAttribute(k, v)
  }
  for (const c of children) {
    if (c === null || c === undefined) continue
    node.append(typeof c === 'string' ? document.createTextNode(c) : c)
  }
  return node
}

export function clear(node: HTMLElement): void {
  while (node.firstChild) node.removeChild(node.firstChild)
}

// ---- load failure ----------------------------------------------------------

// A failed read must never leave the van staring at a blank screen with a raw
// `Failed to fetch` (the whole offline contract used to be write-only: writes
// got a timeout, a persistent queue and an `online` retry; reads got none of
// it, and going offline wiped the day's jobs and did NOT come back when signal
// did). Rules here: say it in Lewis's words, keep whatever we last had on
// screen, and always offer a way back.
// An expired Cloudflare Access session is NOT a signal problem, and "Try
// again" cannot fix it — the read has to say so and offer the one action that
// does. Matched on the wording api.ts throws (SIGNED_OUT_MESSAGE); the api
// module is deliberately not imported here to keep this file dependency-free,
// so a test pins the two together.
export const SIGNED_OUT_PATTERN = /sign-in has expired/i

// `what`: how this screen's saved copy is named in the no-signal line ("Your jobs" on Today; the round elsewhere).
// When a saved copy was made, the way a person says it: "today at 17:40", "yesterday at 17:40", "on Fri at 17:40"
// (Liam, 1 Oct 2026: "your jobs as they were on Fri at 5:40pm").
export function savedWhen(ts: number, now = Date.now()): string {
  const d = new Date(ts)
  const day = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const days = Math.round((day(new Date(now)) - day(d)) / 86_400_000)
  if (days === 0) return `today at ${clockLabel(ts)}`
  if (days === 1) return `yesterday at ${clockLabel(ts)}`
  return `on ${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]} at ${clockLabel(ts)}`
}

export function loadError(root: HTMLElement, message: string, retry: () => void, cachedAt?: number, what = 'The round'): void {
  const signedOut = SIGNED_OUT_PATTERN.test(message)
  const offline = !signedOut && (!navigator.onLine || /fetch|network|timed out|connection/i.test(message))
  const bar = el('div', { class: `load-error${offline ? ' load-error-offline' : ''}${signedOut ? ' load-error-signedout' : ''}` })
  const text = el('span', { class: 'load-error-text' })
  if (signedOut) {
    text.append(
      el('strong', {}, 'Your sign-in has expired. '),
      cachedAt
        ? `Showing the round as it was at ${clockLabel(cachedAt)}. Sign in again to see today's.`
        : 'Sign in again to load your round. Anything you have saved is still waiting to send.',
    )
    const signIn = el('button', { class: 'btn-primary' }, 'Sign in again')
    signIn.addEventListener('click', () => window.location.reload())
    bar.append(text, signIn)
    root.prepend(bar)
    return
  }
  if (offline) {
    // docs/plans/offline/PLAN-v2.md §3: say how old the copy is, and that changes since then aren't here yet.
    text.append(
      el('strong', {}, 'No signal. '),
      cachedAt
        ? `${what} as ${what === 'Your jobs' ? 'they were' : 'it was'} ${savedWhen(cachedAt)}. Changes since then show when you have signal.`
        : 'This day isn\u2019t saved on this phone. It needs signal to show.',
    )
  } else {
    text.append(el('strong', {}, "Couldn't load. "), message)
  }
  const btn = el('button', { class: 'btn-primary' }, 'Try again')
  btn.addEventListener('click', () => {
    btn.setAttribute('disabled', 'disabled')
    btn.textContent = 'trying…'
    retry()
  })
  bar.append(text, btn)
  // Prepend: any cached content already rendered stays visible beneath it.
  root.prepend(bar)
}

// Imported addresses carry access constraints inline — "58 Eastern Road - DO
// NOT BOOK BETWEEN 11-1PM", "35 Bishops Road - DO NOT BOOK BETWEEN 10-12".
// Rendered as one grey string the instruction was the quietest thing on the
// row. Pull a shouted or DO-NOT clause out so it can be styled as a warning.
// Conservative by design: only splits on an explicit separator followed by a
// clause that is shouting or says "do not" / "don't" — a normal address is
// returned untouched.
const ACCESS_CLAUSE = /\s*[-–—,]\s*((?:DO ?NOT|DON'?T|NO )[^-–—]*|[A-Z0-9][A-Z0-9 '()/:£&.+-]{7,})$/

export function splitAccessWarning(address: string): { addr: string; warn: string | null } {
  const m = address.match(ACCESS_CLAUSE)
  if (!m) return { addr: address, warn: null }
  const clause = m[1].trim()
  // Ignore all-caps fragments that are just a postcode or a short token.
  if (clause.length < 8) return { addr: address, warn: null }
  return { addr: address.slice(0, m.index).trim(), warn: clause }
}

// The round's real cycles, as a fixed list. Free text produced 30 distinct
// spellings for about 10 actual cycles ("Adhoc"/"ADHOC", "8 weeks"/"8 week"/
// "8 weeks maybe", "6 months"/"6 Months"/"Every 6 months"), and one customer
// (Alex) ended up with the words "8 weeks" but no number behind them, so the
// app could not work a date out for him. Picking from a list is what stops
// that recurring. The NUMBER is what the app counts forward from; any extra
// detail ("insides every other time", "March") belongs in the Cycle note,
// which is still free text and is not touched by this.
// One dropdown drives BOTH stored fields, so there is nothing to type and no
// second column to keep in step. `days` is the number the app counts forward
// from; `marker` is for the "no regular cycle" answers, which need to be told
// apart from simply never having been asked (66 customers say TBC / Adhoc / ?
// / one off, and those mean different things to a person).
// The short list (Liam, 19 Oct 2026, after user testing: "how often should it be simplified? Are one off and not wanted
// the same thing?"). They are: Ad-hoc, One off and Not wanted all mean no regular visits, never due, so they are one
// choice, "When they ask" (saved as Ad-hoc). To be confirmed is Not set (both mean nobody has agreed one). 10 and 16
// weeks, 3 and 4 months are Custom. Customers saved on any of those keep it and still read as before (cycleLabel).
export const CYCLE_OPTIONS: { label: string; days: number | null; marker?: string }[] = [
  { label: 'not set', days: null },
  // Every fortnight (Liam's testers, 19 Oct 2026: "Every 2 weeks should be an option").
  { label: '2 weeks', days: 14 },
  { label: '4 weeks', days: 28 },
  { label: '6 weeks', days: 42 },
  { label: '8 weeks', days: 56 },
  { label: '12 weeks', days: 84 },
  { label: '6 months', days: 183 },
  { label: 'yearly', days: 365 },
  // A one-off clean (Liam, 27 Oct 2026, How often design D): never due, no check-in booked after it. Apart from When they
  // ask, who stay on the round and get the three-month check-in. Customers saved One off before keep When they ask.
  { label: 'just once', days: null, marker: 'Just once' },
  { label: 'when they ask', days: null, marker: 'Ad-hoc' },
]
// Patio, roof and driveway work comes round every few years (Liam picked A, 15 Oct 2026): those rounds only.
const YEAR_OPTIONS = [{ label: '2 years', days: 730 }, { label: '3 years', days: 1095 }, { label: '5 years', days: 1826 }]
// Choices once offered, still read on the customers saved with them (10 and 16 weeks read so by the weeks rule).
const OLD_LABELS: Record<number, string> = { 91: '3 months', 122: '4 months', 730: '2 years', 1095: '3 years', 1826: '5 years' }
// The words that meant no regular visits (the old choices and the sheet's spellings), and those that meant not agreed.
const NO_REGULAR_WORDS = /^(ad[\s-]?hoc|one[\s-]?off|doesn.?t want regular|not wanted|when they ask)$/
const NOT_AGREED_WORDS = /^(tbc|\?|asked|to be confirmed)$/

// The choices this round offers: a US or Canadian round also has "monthly", counted in calendar months
// (round-config.ts cyclesOn; Step 2 round 2); a patio round, the years. A UK window round's list is exactly CYCLE_OPTIONS.
const MONTHLY_OPTION = { label: 'monthly', days: MONTHLY_DAYS }
const withYears = (list: typeof CYCLE_OPTIONS): typeof CYCLE_OPTIONS => [...list.slice(0, -1), ...YEAR_OPTIONS, ...list.slice(-1)]
const WITH_MONTHLY = [...CYCLE_OPTIONS.slice(0, 3), MONTHLY_OPTION, ...CYCLE_OPTIONS.slice(3)]
const LISTS = { uk: CYCLE_OPTIONS, us: WITH_MONTHLY, ukYears: withYears(CYCLE_OPTIONS), usYears: withYears(WITH_MONTHLY) }
export function cycleOptions(): { label: string; days: number | null; marker?: string }[] {
  return monthlyCycles() ? (yearCycles() ? LISTS.usYears : LISTS.us) : (yearCycles() ? LISTS.ukYears : LISTS.uk)
}

// What the Cycle cell shows: the number's label if there is one, otherwise the
// no-cycle marker, otherwise blank.
export function cycleLabel(days: number | null | undefined, text?: string | null): string {
  // A custom one ("Custom: every 3 weeks") reads in weeks when it is whole weeks (19 Oct 2026).
  if (days) {
    const n = days % 7 === 0 ? days / 7 : days
    return cycleOptions().find((o) => o.days === days)?.label ?? OLD_LABELS[days] ?? `${n} ${days % 7 === 0 ? 'week' : 'day'}${n === 1 ? '' : 's'}`
  }
  const t = (text ?? '').trim().toLowerCase()
  if (!t) return ''
  const hit = cycleOptions().find((o) => o.marker && o.marker.toLowerCase() === t)
  if (hit) return hit.label
  // The old no-regular choices and the sheet's spellings ("ADHOC", "Ad hoc", "One off", "Doesn't want regular").
  if (NO_REGULAR_WORDS.test(t) || /^ad[\s-]?hoc/.test(t)) return 'when they ask'
  return ''
}

// The nearest how-often to a number of days guessed from past visits ("Set to 4 months" for about 18 weeks): a listed
// choice or the old months when one is within a week, otherwise the whole number of weeks, as Custom would save it (the
// short list, 19 Oct 2026, must not turn every 17 weeks into 12).
export function nearestCycleChoice(days: number): { days: number; label: string } | null {
  const named = [...cycleOptions().filter((o) => o.days).map((o) => ({ days: o.days!, label: o.label })), { days: 91, label: '3 months' }, { days: 122, label: '4 months' }]
  const best = named.sort((a, b) => Math.abs(a.days - days) - Math.abs(b.days - days))[0]
  if (best && Math.abs(best.days - days) <= 7) return best
  const weeks = Math.round(days / 7)
  return weeks >= 1 && weeks <= 52 ? { days: weeks * 7, label: cycleLabel(weeks * 7, null) } : best ?? null
}

// The dropdown's value is its index, because two options can share days=null.
export function cycleValueFor(days: number | null | undefined, text?: string | null): string {
  const label = cycleLabel(days, text)
  const i = cycleOptions().findIndex((o) => o.label === label)
  return i >= 0 ? String(i) : ''
}

// A cycle note worth keeping is one that says more than the dropdown already
// does — "8 weeks - insides every other time", "6 months - March", "45 mins".
// A note that merely repeats the label is noise and is not shown.
export function cycleNoteIsMeaningful(days: number | null | undefined, text?: string | null): boolean {
  const t = (text ?? '').trim()
  if (!t) return false
  const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '')
  const label = cycleLabel(days, text)
  if (label && norm(t) === norm(label)) return false
  if (cycleOptions().some((o) => o.marker && norm(o.marker) === norm(t))) return false
  // The old choices' own words (One off, TBC) are the choice, not a note.
  if (NO_REGULAR_WORDS.test(t.toLowerCase()) || NOT_AGREED_WORDS.test(t.toLowerCase())) return false
  return norm(t) !== norm(cycleLabel(days) || '')
}

// A transient one-line confirmation. Used where something happened that the
// user did not directly ask for (the auto-booked next visit) — a window.alert
// would be a modal to dismiss one-handed in a van, and silence would make a
// booking appear from nowhere.
// What to say after a clean is logged and the app placed the next entry. The
// two cases are not the same promise, so they do not get the same sentence.
export function nextBookedMessage(next: { date: string; source?: string | null }): string {
  return next.source === 'tentative'
    ? `No cycle set: a contact reminder for ${dayLabel(next.date)}, not a visit`
    : `Next visit suggested by Roundly for ${dayLabel(next.date)}, not booked`
}

let confirmationRound: string | null = null
export function setConfirmationRound(name: string | null): void { confirmationRound = name }
export function roundConfirmation(message: string): string {
  if (!confirmationRound) return message
  if (message === 'Customer added') return `Added to ${confirmationRound}`
  if (message === 'Visit saved') return `Saved in ${confirmationRound}`
  if (message === 'Visit booked') return `Booked in ${confirmationRound}`
  if (/^(Booked |Next visit |Converted: |Visit saved|Completed work recorded|Payment changed|Marked .+ (paid by|done)|Counted |Added to |Customer added)/.test(message)) return `${message.replaceAll(String.fromCharCode(8212), ",")} in ${confirmationRound}`
  if (message.includes(' is a customer')) return `${message.replaceAll(String.fromCharCode(8212), ',')} in ${confirmationRound}`
  return message
}
export function visitSavedMessage(existing: boolean): string {
  return confirmationRound && !existing ? 'Visit booked' : 'Visit saved'
}
export function toast(message: string): void {
  message = roundConfirmation(message)
  document.querySelector('#toast')?.remove()
  const t = el('div', { id: 'toast', role: 'status' }, message)
  document.body.append(t)
  setTimeout(() => t.remove(), 5000)
}

export function clockLabel(ts: number): string {
  const d = new Date(ts)
  const mm = String(d.getMinutes()).padStart(2, '0')
  return clockWords(d.getHours(), mm)
}

// ---- money ----------------------------------------------------------------

// Thousands get a comma, "£2,225.00", as the Money tiles already did: Messages
// read "£2225.00" and Money's September row "£1390.00" (Liam's Dropby
// look-over, 29 Sep 2026). parseMoney reads the comma back.
// The round's own money sign (round-config.ts, Step 1): "£" for every UK round, exactly as
// before ("£-12.50" included); "$" for a US or Canadian round. Only the sign changes.
export function money(pence: number | null | undefined): string {
  if (pence === null || pence === undefined) return '—'
  const [whole, pennies] = (pence / 100).toFixed(2).split('.')
  return `${currencySymbol()}${whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${pennies}`
}

// Parses a user-typed money string ("25", "£25.00", "25.5", "1,250") into
// pence. Returns null for blank, throws on nonsense. Commas are ONLY
// legal as thousands separators (codex MEDIUM-9: stripping all commas
// turned "1,2,3" into £123.00 and "£,25" into £25.00 — valid-looking
// wrong money, in payroll close-outs too).
// The round's own sign may lead ("£25" in the UK); the other one is refused, so dollars are
// never taken as pounds (Step 1).
export function parseMoney(input: string): number | null {
  const sign = currencySymbol()
  const words = sign === '£' ? 'money as pounds, e.g. 25 or 25.50' : 'money as dollars, e.g. 25 or 25.50'
  let t = input.trim()
  if (t.startsWith(sign)) t = t.slice(sign.length)
  t = t.replace(/\s/g, '')
  if (t === '') return null
  const plain = /^\d+(\.\d{1,2})?$/
  const withCommas = /^\d{1,3}(,\d{3})+(\.\d{1,2})?$/
  if (plain.test(t)) return Math.round(parseFloat(t) * 100)
  if (withCommas.test(t)) return Math.round(parseFloat(t.replace(/,/g, '')) * 100)
  throw new Error(words)
}

// ---- "Couldn't do it" (migration 0008) --------------------------------------

// The stored reason is a CHECK-list code; every surface — the worker's card,
// the owner's diary, the customer page and History — renders the words the
// worker would have said. An unknown code falls through as itself rather
// than disappearing.
export const BLOCKED_REASON_LABELS: Record<string, string> = {
  not_in: 'Not in',
  no_access: 'No access',
  weather: 'Weather',
  customer_cancelled: 'Customer cancelled',
  other: 'Other',
}

export function blockedReasonLabel(code: string | null | undefined): string {
  if (!code) return ''
  return BLOCKED_REASON_LABELS[code] ?? code
}

// "Ran out of time" (Liam, 22 Oct 2026, docs/plans/rearrange/PLAN.md): a Couldn't do it of Other whose note starts with
// these words, as Finish my day first saved it (0008's list of five codes is not widened). One rule for every screen.
export const OUT_OF_TIME = 'Ran out of time'
const OUT_OF_TIME_START = /^ran out of time\b[.:,;\s-]*/i
export function isOutOfTime(reason: string | null | undefined, note: string | null | undefined): boolean {
  return reason === 'other' && OUT_OF_TIME_START.test((note ?? '').trim())
}
// What a typed note is saved as with Ran out of time: "Ran out of time", or "Ran out of time. <note>". The note box takes
// OUT_OF_TIME_ROOM characters then, so the whole fits the server's 300.
export const OUT_OF_TIME_ROOM = 300 - `${OUT_OF_TIME}. `.length
export function outOfTimeNote(typed: string): string {
  const t = typed.trim()
  return t ? `${OUT_OF_TIME}. ${t}` : OUT_OF_TIME
}
// The reason in words, and the note as it reads beside them (Ran out of time is not said twice).
export function blockedWords(reason: string | null | undefined, note: string | null | undefined): string {
  return isOutOfTime(reason, note) ? OUT_OF_TIME : blockedReasonLabel(reason)
}
export function blockedNoteText(reason: string | null | undefined, note: string | null | undefined): string {
  const n = (note ?? '').trim()
  return isOutOfTime(reason, n) ? n.replace(OUT_OF_TIME_START, '').trim() : n
}
// The answers Couldn't do it offers, Ran out of time first (it is the one a full day runs into). 'time' is never stored.
export const BLOCKED_CHOICES: [string, string][] = [['time', OUT_OF_TIME], ...Object.entries(BLOCKED_REASON_LABELS)]
export function blockedChoiceOf(reason: string | null | undefined, note: string | null | undefined): string | null {
  return !reason ? null : isOutOfTime(reason, note) ? 'time' : reason
}
export function blockedSaveOf(choice: string, typed: string): { blocked_reason: string; blocked_note: string | null } {
  if (choice === 'time') return { blocked_reason: 'other', blocked_note: outOfTimeNote(typed) }
  return { blocked_reason: choice, blocked_note: typed.trim() || null }
}

// blocked_at is 'yyyy-mm-dd HH:MM:SS' UTC (sqliteNow), but a clock on a visit
// card is read against the phone's own clock — parse as UTC, print local.
export function blockedClock(at: string | null | undefined): string {
  if (!at) return ''
  const d = new Date(`${at.slice(0, 10)}T${at.slice(11, 19)}Z`)
  if (Number.isNaN(d.getTime())) return at
  return shownClock(d) // the round's clock, not the phone's (Step 1), written the round's way (round 7)
}

// The owner-view badge: "Couldn't do it · Not in (14:20)". The worker's card
// uses the same parts with its own separator styling.
export function blockedBadgeText(cl: { blocked_reason?: string | null; blocked_at?: string | null; blocked_note?: string | null }): string {
  const reason = blockedWords(cl.blocked_reason, cl.blocked_note)
  if (!reason) return ''
  const clock = blockedClock(cl.blocked_at)
  return `Couldn't do it · ${reason}${clock ? ` (${clock})` : ''}`
}

// ---- dates ----------------------------------------------------------------

// The ROUND's day (round-config.ts, Step 1, 29 Sep 2026), the same day the server works to:
// the phone's own day disagreed with it near midnight whenever the phone was in another zone.
export function todayISO(): string {
  return roundDay()
}

export function addDaysISO(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// The Monday of the week a date falls in. A visit that has a week but no day
// still has to carry a date (every visit does), so it carries the Monday —
// one date per week, so two visits put in the same week read as the same week.
export function mondayOf(iso: string): string {
  const d = new Date(`${iso}T12:00:00`)
  return addDaysISO(iso, -((d.getDay() + 6) % 7))
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "2026-08-04" → "Tue 4 Aug" (the sheet's day-label style), with the YEAR
// appended once the date is outside the current one: a clean history reading
// "Wed 26 Feb" for a 2025 job made an 18-month-lapsed customer look recent.
export function dayLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00`)
  return weekdayDayMonth(WEEKDAYS[d.getDay()]!, d.getDate(), MONTHS[d.getMonth()]!, String(d.getFullYear()) === todayISO().slice(0, 4) ? undefined : d.getFullYear())
}

// Imported phone values are free text: "Canvassing", "Ads", "07000 000000 - NO
// WA", "07000 000001 (name)". A tel: href built from those either does nothing
// or dials garbage — so build the href from the digits and show the original
// text as-is. Returns null when there is no plausible number to dial.
export function telHref(raw: string | null | undefined): string | null {
  if (!raw) return null
  const digits = raw.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '')
  return digits.replace(/\D/g, '').length >= 7 ? `tel:${digits}` : null
}

// "2026-05-15" → "15 May" (+ year when it isn't this year). The grid used to
// print the raw "2026-05-15" from the database, which is unreadable at a glance
// in a column you are scanning down.
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return String(iso)
  if (String(d.getFullYear()) === todayISO().slice(0, 4)) return dayMonth(d.getDate(), MONTHS[d.getMonth()]!)
  // A short year in a column: "15 May 26" / "May 15 '26".
  return monthFirst() ? `${MONTHS[d.getMonth()]} ${d.getDate()} '${String(d.getFullYear()).slice(2)}` : `${d.getDate()} ${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`
}

// A visit's time as a screen writes it: "14:30", or "2:30 PM" in a US or Canadian round (Step 2 round 7). Null when it
// has none or the round shows no set times. For sorting, keys and comparisons use visibleTime, never this.
export function shownVisitTime(visit: { time_slot?: string | null } | null | undefined): string | null {
  const time = visibleTime(visit)
  return time ? shownTime(time) : null
}

// The day stamp saved at the start of a note line ("10 Sep: rebook text 1", "10 Sep 25" for another year). Always this
// form, whatever the round: Messages and New jobs read their own note lines back by it. A US screen shows it turned
// round (noteWords); only what a screen writes changes (Step 2 round 7).
export function noteDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return String(iso)
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`
  return String(d.getFullYear()) === todayISO().slice(0, 4) ? base : `${base} ${String(d.getFullYear()).slice(2)}`
}

// A saved note line as a screen writes it: in a US or Canadian round its days read month first ("Sep 10: rebook text 1",
// "moved from Oct 11 to Oct 14", "Sep 10 '25") and a stamped clock "2:05 PM". The saved note is never changed, and a UK
// round's reads exactly as saved (Step 2 round 7).
const NOTE_DAY = /\b(\d{1,2}) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sept|Sep|Oct|Nov|Dec)\b(?: (\d{2})(?=:(?!\d)|;| to | or |$))?/g
export function noteWords(line: string): string {
  if (!monthFirst()) return line
  return shownTime(line.replace(NOTE_DAY, (_m, d: string, mon: string, yy?: string) => `${mon} ${d}${yy ? ` '${yy}` : ''}`))
}

export function isPastOrToday(iso: string): boolean {
  return iso <= todayISO()
}

// Roundly's privacy page (Liam approved the wording 2026-09-25). It lives on
// the public Roundly site, not in the app, so anyone can read it before they
// have signed in. Linked from sign-up, the account menu and Help.
export const PRIVACY_URL = 'https://roundly.work/privacy'
